import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import {
  selectIsBookkeepingRow,
  selectIsLatestAssistantMessage,
  selectMessage,
  selectTurn,
  type ChatRootState,
} from '@/slices/chat/chatSelectors';
import type { ChatBlock } from '@/slices/chat/chatSlice';
import { addCitationBlock } from '@/slices/chat/addCitationBlock';
import type { AppDispatch } from '@/store/store';
import { useIsCourseStudent } from '@/instances/useInstance';
import type { ChatBlockPlacement, ChatCitation } from '@/types/chatStream';
import ChatAnswerFooter from './ChatAnswerFooter';
import ChatAttachedImages from './ChatAttachedImages';
import ChatContextLabel from './ChatContextLabel';
import ChatFollowups from './ChatFollowups';
import { useIsMcGrawHill } from '../../../../mcgrawHill/mcgrawHill'; // mcgraw-hill
import { useIsMcGrawHillClinical } from '../../../../mcgrawHill/mcgrawHillModeStore'; // mcgraw-hill
import {
  layoutMcGrawHillAnswer,
  stripQuoteMarkers,
} from '../../../../mcgrawHill/mcgrawHillQuoteMarkers'; // mcgraw-hill
import McGrawHillAnswerBody from '../../../../mcgrawHill/McGrawHillAnswerBody'; // mcgraw-hill
import {
  hasCiteMarkers,
  numberMcGrawHillSources,
  stripCiteMarkers,
} from '../../../../mcgrawHill/mcgrawHillCitations'; // mcgraw-hill
import McGrawHillSources from '../../../../mcgrawHill/McGrawHillSources'; // mcgraw-hill
import { McGrawHillCiteContext } from '../../../../mcgrawHill/McGrawHillCiteBadge'; // mcgraw-hill
import ChatMarkdown from './ChatMarkdown';
import ChatMessageActions from './ChatMessageActions';
import ChatTurnActivity from './ChatTurnActivity';
import ChatTurnNotice from './ChatTurnNotice';
import type { ChatQuestionFollowup } from './blocks/chatQuestionFollowup';
import { parseQuestionBlockPayload } from './blocks/chatBlockPayload';
import { planAttachmentRetry } from './chatAttachmentRetry';
import { citationBlockIds } from './chatSourceLabels';
import {
  htmlToPlainText,
  looksLikeLegacyHtml,
  stripLegacyCitationMarkers,
} from './chatText';

export interface ChatMessageItemProps {
  messageId: string;
  /** Re-run this message's turn. Omitted while another turn is in flight. */
  onRetry?: (assistantMessageId: string) => void;
  /**
   * Renders the turn's blocks for one placement slot. Called once for each
   * slot with the message's whole block list, so the renderer (ORA-448's
   * registry) owns both the per-turn cap and which blocks belong in which
   * slot. `inline` sits above the answer, `end` below it.
   */
  renderBlocks?: (
    blocks: ChatBlock[],
    placement: ChatBlockPlacement,
    onQuestionAnswered?: (question: ChatQuestionFollowup) => void,
    onBeforeContentExpand?: () => void
  ) => ReactNode;
  /**
   * Called as a streaming answer grows, so the thread can keep the newest text
   * in view. The thread passes a stable callback and never subscribes to the
   * text itself — only the row that is streaming re-renders per delta.
   */
  onContentGrow?: () => void;
  /** Preserve the thread's position before a block grows in place. */
  onBeforeBlockExpand?: () => void;
  /**
   * Open one of the answer's sources. Supplied by a surface that can show the
   * source in place — the document viewer's split pane scrolls its own viewer
   * to a cited page. Without it the citations render as plain labels.
   */
  onSelectCitation?: (citation: ChatCitation) => void;
  /** Re-read the conversation after one of its presigned images expires. */
  onReloadImages?: () => void;
  /**
   * Ask the next question from a follow-up chip (ORA-606), through the same
   * send path the composer uses. Omitted when this surface cannot send one —
   * the daily cap is spent — and the chips then stay away.
   */
  onSendFollowup?: (
    prompt: string,
    anchor?: ChatQuestionFollowup['anchor']
  ) => void;
  /**
   * The student is writing their own question. They have already decided what
   * to ask, so the suggestions step aside until the box is empty again.
   */
  isDrafting?: boolean;
  /** Dev harness only: draw the turn's tool calls under the status line. */
  showToolActivity?: boolean;
}

const NO_CITATIONS: ChatCitation[] = [];
const ADD_SOURCE_FAILED = "Couldn't add that source. Try again.";

const questionBlockIds = (blocks: ChatBlock[]) =>
  blocks.flatMap((block) =>
    block.kind === 'question' && parseQuestionBlockPayload(block.payload)
      ? [block.id]
      : []
  );

/**
 * One message in the thread.
 *
 * The user's own message is a bubble; the assistant's is full width, with the
 * turn's activity above it and — once the answer has stopped streaming — the
 * answer footer under it, carrying the action bar (ORA-464) and the answer's
 * sources (ORA-601). Legacy assistant text is the same renderer with the old
 * inline markers stripped out first, or plain text when the message predates
 * markdown and was stored as HTML.
 */
const ChatMessageItem = ({
  messageId,
  onRetry,
  renderBlocks,
  onContentGrow,
  onBeforeBlockExpand,
  onSelectCitation,
  onReloadImages,
  onSendFollowup,
  isDrafting = false,
  showToolActivity = false,
}: ChatMessageItemProps) => {
  const dispatch = useDispatch<AppDispatch>();
  // Only a course student's companion (Courses V1) reads the model's
  // `[[watch …]]` token; everyone else's answers render as written.
  const isCourseStudent = useIsCourseStudent();

  const message = useSelector((state: ChatRootState) =>
    selectMessage(state, messageId)
  );

  const turn = useSelector((state: ChatRootState) =>
    selectTurn(state, message?.turnId ?? null)
  );

  const retrySource = useSelector((state: ChatRootState) => {
    const assistant = state.chat.messages[messageId];
    if (!assistant) return null;

    const messageIds =
      state.chat.conversations[assistant.conversationId]?.messageIds ?? [];

    const assistantIndex = messageIds.indexOf(messageId);

    for (let index = assistantIndex - 1; index >= 0; index -= 1) {
      const candidate = state.chat.messages[messageIds[index]];
      if (candidate?.role === 'user') return candidate;
    }

    return null;
  });

  // Regenerate replaces an answer, and only the answer a thread ends on can be
  // replaced in place — see the selector.
  const isLatestAnswer = useSelector((state: ChatRootState) =>
    selectIsLatestAssistantMessage(state, messageId)
  );

  // Read the block map and resolve the ids here rather than through a
  // memoized selector: one selector instance is shared by every message in the
  // thread, and a per-message array built inside it would be a fresh reference
  // on each render.
  const blocksById = useSelector((state: ChatRootState) => state.chat.blocks);
  const isMcGrawHill = useIsMcGrawHill();
  const isMcGrawHillClinical = useIsMcGrawHillClinical(isMcGrawHill);
  const blockIds = message?.blockIds;

  const blocks = useMemo(
    () =>
      (blockIds ?? []).flatMap((id) =>
        blocksById[id] ? [blocksById[id]] : []
      ),
    [blockIds, blocksById]
  );

  const [questionFollowups, setQuestionFollowups] = useState<
    ChatQuestionFollowup[]
  >([]);

  const handleQuestionAnswered = useCallback(
    (question: ChatQuestionFollowup) => {
      setQuestionFollowups((current) =>
        current.some((attempt) => attempt.blockId === question.blockId)
          ? current
          : [...current, question]
      );
    },
    []
  );

  const servedQuestionBlockIds = useMemo(
    () => questionBlockIds(blocks),
    [blocks]
  );

  const currentQuestionFollowups = questionFollowups.filter((attempt) =>
    servedQuestionBlockIds.includes(attempt.blockId)
  );

  const hasUnansweredQuestion = servedQuestionBlockIds.some(
    (blockId) =>
      !currentQuestionFollowups.some((attempt) => attempt.blockId === blockId)
  );

  const questionFollowup =
    currentQuestionFollowups[currentQuestionFollowups.length - 1];

  const hasMissedQuestion = questionFollowup?.firstAttemptCorrect === false;
  /**
   * Which of this answer's sources are already blocks somewhere in the
   * conversation (ORA-514, ORA-601): served by this turn, served by a later
   * one, or added from the footer before a reload. Their chips read "Shown
   * above" and go back to the block instead of offering a plus.
   */
  const conversationId = message?.conversationId;
  const citations = message?.citations ?? NO_CITATIONS;

  const shownBlockIds = useMemo(
    () =>
      citationBlockIds(
        citations,
        Object.values(blocksById).filter(
          (block) => block.conversationId === conversationId
        )
      ),
    [blocksById, citations, conversationId]
  );

  /**
   * Add a source as a block. The wire names the server's message id, so an
   * answer the server has not named yet has nothing to add to. A refused add
   * is said out loud and the pill re-enables itself.
   */
  const handleAddCitation = useCallback(
    async (citation: ChatCitation) => {
      const result = await dispatch(
        addCitationBlock({ messageId, citationId: citation.id })
      );

      if (!result.ok) enqueueSnackbar(ADD_SOURCE_FAILED, { variant: 'error' });
      return result.ok;
    },
    [dispatch, messageId]
  );

  const rawText = message?.text ?? '';
  const isLegacy = message?.isLegacy ?? false;
  // V1 messages were persisted as HTML. Nothing here renders markup, so an
  // HTML body is read as text instead of being shown with its tags.
  const isLegacyHtml = isLegacy && looksLikeLegacyHtml(rawText);

  const text = useMemo(() => {
    if (!isLegacy) return rawText;

    const stripped = stripLegacyCitationMarkers(rawText);
    return isLegacyHtml ? htmlToPlainText(stripped) : stripped;
  }, [isLegacy, isLegacyHtml, rawText]);

  const isStreaming = message?.isStreaming ?? false;

  // mcgraw-hill: `[[quote:<id>]]` markers render as text and quote cards in
  // order; `[[cite:…]]` markers get numbered badges and a Sources list.
  const mcgrawHillSources = useMemo(
    () =>
      isMcGrawHill && hasCiteMarkers(text)
        ? numberMcGrawHillSources(text, citations)
        : null,
    [citations, isMcGrawHill, text]
  );

  const shownText = mcgrawHillSources?.text ?? text;

  const openMcGrawHillSource = useCallback(
    (number: number) => {
      const source = mcgrawHillSources?.sources.find(
        (entry) => entry.number === number
      );

      if (source) {
        onSelectCitation?.({ ...source.citation, highlightExcerpt: true });
      }
    },
    [mcgrawHillSources, onSelectCitation]
  );

  const mcgrawHillCiteContext = useMemo(
    () =>
      mcgrawHillSources
        ? { open: openMcGrawHillSource, roles: mcgrawHillSources.roles }
        : null,
    [mcgrawHillSources, openMcGrawHillSource]
  );

  const mcgrawHillLayout = useMemo(
    () =>
      isMcGrawHill
        ? layoutMcGrawHillAnswer(shownText, blocks, isStreaming)
        : null,
    [blocks, isMcGrawHill, isStreaming, shownText]
  );

  const slotBlocks = mcgrawHillLayout?.slotBlocks ?? blocks;

  /**
   * A row the server wrote for its own bookkeeping: the `set_result` line a
   * return leaves (ORA-681), the content a question badge appended (ORA-546),
   * the review set "Study Now" minted (ORA-831). None of them is an answer —
   * there is nothing to copy, read aloud, rate or re-run — so each is drawn as
   * the content it is, without the answer footer's (invisible until hovered,
   * but still 44px tall) action bar under it.
   */
  const isBookkeepingRow = useSelector((state: ChatRootState) =>
    selectIsBookkeepingRow(state, messageId)
  );

  /**
   * The turn is still writing. Wider than `isStreaming`, which `text.done`
   * clears while the runtime is still persisting and titling (ORA-459) —
   * `text.done` itself replaces the text with the authoritative version, and
   * blocks can still land after it, so the thread must keep following until
   * the turn is actually over.
   */
  const isTurnRunning =
    turn?.status === 'requested' || turn?.status === 'streaming';

  /**
   * The thread coalesces this to one layout read per frame, so calling it on
   * every render of a streaming row is cheap.
   */
  useEffect(() => {
    if (isStreaming || isTurnRunning) onContentGrow?.();
  }, [isStreaming, isTurnRunning, onContentGrow, text]);

  if (!message) return null;

  if (message.role === 'user') {
    return (
      <li
        className="flex flex-col items-end"
        data-testid="chat-message-user"
        data-message-id={message.id}
      >
        {message.contextLabel ? (
          <ChatContextLabel context={message.contextLabel} />
        ) : null}
        <div className="chat-message-user__bubble max-w-[85%] whitespace-pre-wrap break-words rounded-2xl bg-surface-highlight px-3 py-2 text-sm text-fg-primary">
          <ChatAttachedImages
            attachments={message.attachments}
            onReload={onReloadImages}
          />
          {message.text}
        </div>
      </li>
    );
  }

  /**
   * The answer is finished and there is one: the action bar's whole condition
   * (ORA-464). A turn that failed or was stopped is not a completed answer —
   * it offers "Try again" on its notice instead — and neither is one still
   * streaming. Hydrated history has no turn, so the server id is what says it
   * landed.
   *
   * "There is one" means prose OR blocks: a turn can answer entirely by
   * serving content (a question, a flashcard) and say nothing in words, and
   * that answer is as re-runnable and as ratable as any other. The bar itself
   * drops Copy and Read aloud when there is no prose for them.
   */
  const isCompletedAnswer =
    !isStreaming &&
    (text !== '' || blocks.length > 0) &&
    (turn ? turn.status === 'done' : Boolean(message.serverId));

  // Try again re-runs the LAST answer that actually landed, and a legacy answer
  // is read-only, since the old pipeline is gone.
  const canOfferTryAgain =
    isCompletedAnswer && Boolean(onRetry) && !isLegacy && isLatestAnswer;

  const attachmentRetry = retrySource ? planAttachmentRetry(retrySource) : null;
  const canRetrySource = Boolean(onRetry) && Boolean(attachmentRetry?.canRetry);
  const canTryAgain = canOfferTryAgain && canRetrySource;

  const retryDisabledReason = canOfferTryAgain
    ? (attachmentRetry?.disabledReason ?? undefined)
    : undefined;

  /**
   * Only the answer the thread ends on offers the next question, and only once
   * its turn is over: chips under an older answer would fork the conversation
   * from the middle, and chips under an answer still being written would move
   * while it is read. A missed embedded question is the one intentional local
   * substitute: its three next-study prompts are tied to that exact question.
   */
  const followups =
    // mcgraw-hill: none in Clinical mode.
    !isMcGrawHillClinical &&
    onSendFollowup &&
    turn &&
    !isDrafting &&
    isLatestAnswer &&
    turn.status === 'done' &&
    !hasUnansweredQuestion &&
    (turn.followups.length > 0 || hasMissedQuestion) ? (
      <ChatFollowups
        followups={turn.followups}
        conversationId={message.conversationId}
        turnId={turn.id}
        onSend={onSendFollowup}
        question={questionFollowup}
      />
    ) : null;

  const handleTryAgain = () => {
    if (!canRetrySource) return;
    if (attachmentRetry?.notice)
      enqueueSnackbar(attachmentRetry.notice, { variant: 'info' });
    onRetry?.(message.id);
  };

  return (
    // mcgraw-hill: numbered source badges open their source.
    <McGrawHillCiteContext.Provider value={mcgrawHillCiteContext}>
      <li
        // Keep absolute sr-only live regions inside the thread's scroll boundary.
        // Without a local containing block they can enlarge the document instead.
        className="group relative flex flex-col text-sm text-fg-primary"
        data-testid="chat-message-assistant"
        data-message-id={message.id}
      >
        {turn ? (
          <ChatTurnActivity turn={turn} showToolActivity={showToolActivity} />
        ) : null}

        {renderBlocks && slotBlocks.length > 0
          ? renderBlocks(
              slotBlocks,
              'inline',
              handleQuestionAnswered,
              onBeforeBlockExpand
            )
          : null}

        {mcgrawHillLayout ? (
          <McGrawHillAnswerBody
            layout={mcgrawHillLayout}
            blocks={blocks}
            renderBlock={
              renderBlocks &&
              ((block) =>
                renderBlocks(
                  [block],
                  block.placement,
                  handleQuestionAnswered,
                  onBeforeBlockExpand
                ))
            }
            isStreaming={isStreaming}
            isTurnRunning={isTurnRunning}
            watchTokens={isCourseStudent}
          />
        ) : shownText ? (
          <ChatMarkdown
            text={shownText}
            isStreaming={isStreaming}
            isTurnRunning={isTurnRunning}
            watchTokens={isCourseStudent}
          />
        ) : null}

        {mcgrawHillSources && !isStreaming ? (
          <McGrawHillSources
            sources={mcgrawHillSources.sources}
            onOpen={onSelectCitation}
          />
        ) : null}

        {turn ? (
          <span role="status" aria-live="polite" className="sr-only">
            {isStreaming ? 'Ora is answering.' : 'Ora has finished answering.'}
          </span>
        ) : null}

        {renderBlocks && slotBlocks.length > 0
          ? renderBlocks(
              slotBlocks,
              'end',
              handleQuestionAnswered,
              onBeforeBlockExpand
            )
          : null}

        {turn ? (
          <ChatTurnNotice
            turn={turn}
            onRetry={canRetrySource ? handleTryAgain : undefined}
          />
        ) : null}

        {/* The footer is the end of the answer: what to do with it, and what it
          was built from. It stays away until the answer has stopped moving,
          so nothing shifts under a student who is still reading. */}
        {!isStreaming && !isBookkeepingRow ? (
          <ChatAnswerFooter
            // mcgraw-hill: the numbered Sources list stands in for the chips.
            citations={mcgrawHillSources ? NO_CITATIONS : citations}
            shownBlockIds={shownBlockIds}
            onSelect={onSelectCitation}
            onAdd={
              message.serverId && !isLegacy ? handleAddCitation : undefined
            }
            followups={followups}
            actions={
              isCompletedAnswer ? (
                <ChatMessageActions
                  messageId={message.id}
                  // mcgraw-hill: Copy and read-aloud skip the quote markers.
                  text={
                    mcgrawHillLayout || mcgrawHillSources
                      ? stripCiteMarkers(stripQuoteMarkers(text))
                      : text
                  }
                  serverMessageId={message.serverId}
                  isLegacy={isLegacy}
                  watchTokens={isCourseStudent}
                  onTryAgain={canTryAgain ? handleTryAgain : undefined}
                  tryAgainDisabledReason={retryDisabledReason}
                />
              ) : null
            }
          />
        ) : null}
      </li>
    </McGrawHillCiteContext.Provider>
  );
};

export default ChatMessageItem;
